import React, { useMemo } from 'react';
import { Modal } from '@/components/Modal';
import { FbCampaign, getInsights } from '@/services/facebook-campaigns.service';
import { formatMetaCount, formatMetaPercent, formatMetaCurrency } from '@/utils/currency';
import { Crown, BarChart3 } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  open: boolean;
  onClose: () => void;
  campaigns: FbCampaign[];
  currency: string;
}

type Metric = 'spend' | 'impressions' | 'clicks' | 'reach' | 'ctr' | 'cpc' | 'cpm' | 'frequency' | 'conversions';

const METRIC_LABELS: Record<Metric, string> = {
  spend: 'Spend',
  impressions: 'Impressions',
  clicks: 'Clicks',
  reach: 'Reach',
  ctr: 'CTR',
  cpc: 'CPC',
  cpm: 'CPM',
  frequency: 'Frequency',
  conversions: 'Conversions',
};

/**
 * For each metric, decide whether higher or lower is better. Used to pick the
 * "winner" cell highlighting.
 *   - higher-better: spend (more reach), impressions, clicks, reach, ctr, conversions, frequency (mild)
 *   - lower-better: cpc, cpm
 */
const HIGHER_IS_BETTER: Record<Metric, boolean> = {
  spend: false,         // not a winner — just informational
  impressions: true,
  clicks: true,
  reach: true,
  ctr: true,
  cpc: false,
  cpm: false,
  frequency: true,
  conversions: true,
};

function num(v?: string): number {
  if (!v) return 0;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function sumActions(actions?: Array<{ action_type: string; value: string }>): number {
  if (!actions?.length) return 0;
  let total = 0;
  for (const a of actions) {
    const v = parseFloat(a.value);
    if (Number.isFinite(v)) total += v;
  }
  return total;
}

function formatMetric(m: Metric, value: number, currency: string): string {
  if (m === 'spend' || m === 'cpc' || m === 'cpm') return formatMetaCurrency(String(value), currency);
  if (m === 'ctr')      return formatMetaPercent(String(value));
  if (m === 'frequency') return value.toFixed(2);
  return formatMetaCount(String(value));
}

const METRICS: Metric[] = ['spend', 'impressions', 'clicks', 'reach', 'ctr', 'cpc', 'cpm', 'frequency', 'conversions'];

export const CompareCampaignsModal: React.FC<Props> = ({ open, onClose, campaigns, currency }) => {
  const rows = useMemo(() => {
    return METRICS.map((m) => {
      const values = campaigns.map((c) => {
        const ins = getInsights(c);
        if (!ins) return 0;
        if (m === 'conversions') return sumActions(ins.actions);
        return num((ins as Record<string, unknown>)[m] as string | undefined);
      });
      // Pick winner
      let winnerIdx = -1;
      const valid = values.filter((v) => v > 0);
      if (valid.length >= 2) {
        if (HIGHER_IS_BETTER[m]) {
          winnerIdx = values.indexOf(Math.max(...values.filter((v) => v > 0)));
        } else {
          // Lower-better — among non-zero values
          const minOfPositive = Math.min(...values.filter((v) => v > 0));
          winnerIdx = values.indexOf(minOfPositive);
        }
      }
      return { metric: m, values, winnerIdx };
    });
  }, [campaigns]);

  if (campaigns.length < 2) return null;

  return (
    <Modal open={open} onClose={onClose} title="Compare campaigns" widthClass="max-w-4xl">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wide text-text-muted">
                Metric
              </th>
              {campaigns.map((c) => (
                <th key={c.id} className="px-3 py-2 text-left">
                  <div className="text-2xs font-semibold uppercase tracking-wide text-text-muted truncate">{c.objective}</div>
                  <div className="mt-0.5 truncate text-[13px] font-semibold text-text-primary">{c.name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ metric, values, winnerIdx }) => (
              <tr key={metric} className="border-b border-border last:border-b-0">
                <td className="px-3 py-2 text-2xs font-semibold uppercase tracking-wide text-text-muted">
                  {METRIC_LABELS[metric]}
                </td>
                {values.map((v, i) => (
                  <td
                    key={i}
                    className={clsx(
                      'px-3 py-2 tabular-nums',
                      winnerIdx === i ? 'bg-success-soft font-semibold text-success' : 'text-text-primary',
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      {formatMetric(metric, v, currency)}
                      {winnerIdx === i && <Crown size={11} className="text-success" />}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-lg border border-info/30 bg-info-soft px-3 py-2">
        <BarChart3 size={13} className="mt-0.5 shrink-0 text-info" />
        <p className="text-2xs text-info">
          Crown marks the better number for each metric. CPC/CPM lower-is-better; everything else higher-is-better.
          Currency: <strong>{currency}</strong>. Spend doesn't crown a winner (it's informational).
        </p>
      </div>
    </Modal>
  );
};
